/* eslint-disable no-unused-vars */

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  GridSize,
  TextField,
  Typography
} from '@material-ui/core';
import { useFormik } from 'formik';
import moment from 'moment';
import { DatePickerRangeComponentV2 } from 'src/components';
import { useEventSubscriptions } from 'src/hooks/event-subscriptions/use-event-subscription';
import {
  CreateEventSubscriptionRequest,
  UpdateEventSubscriptionRequest
} from 'src/redux/slices/event-subscriptions';
import {
  CreateVoucherRequest,
  VoucherScope
} from 'src/redux/slices/voucher/types';
import { usePermissions, useSnackBar } from 'src/hooks';
import { constants } from 'src/redux/slices/voucher';
import { decodeBase64Html, formatingDate } from 'src/utils';
import { DatePickerRangeComponentV3 } from 'src/components/date/DatePickerRangeComponentV3';
import { DescriptionHtmlDialog } from 'src/components/dialogs/DescriptionHtmlDialog';
import { MultipleSelectedDropdown } from 'src/components/dropdown/MultipleSelectedDropdown';
import { GenericMenuOptionV2 } from 'src/types';
import { useEmailer } from 'src/hooks/email/useEmailer';
import { Autocomplete } from '@material-ui/lab';
import { EmailerListItem } from 'src/redux/slices/emailer';

interface Props {
  id?: number | null;
  isCreateForm?: boolean;
}

interface SpecFieldProps {
  label: string;
  value: React.ReactNode;
  xs?: boolean | GridSize;
}

export const EventForm: FC<Props> = ({ id = null, isCreateForm = false }) => {
  const {
    eventDetails,

    createEvent,
    updateEvent,
    getEventDetails
  } = useEventSubscriptions();
  const snackbar = useSnackBar();
  const { emailerList, isLoading, getEmailerList } = useEmailer();
  const { canUpdateEvent } = usePermissions();

  const initialValues:
    | CreateEventSubscriptionRequest
    | UpdateEventSubscriptionRequest = {};

  const dynamicSaveLabel = isCreateForm ? 'Create Event' : 'Save Changes';

  const [isOpenDescriptionModal, setIsOpenDescriptionModal] = useState<boolean>(
    false
  );

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values, { resetForm }) => {
      if (!formik.dirty) {
        snackbar.show({
          severity: 'error',
          message: 'No Changes Detected'
        });
        return;
      }

      const success = isCreateForm
        ? await createEvent(values)
        : await updateEvent(values);

      if (isCreateForm && success) {
        resetForm();
      } else if (!isCreateForm && success) {
        id ? getEventDetails(id) : null; // refresh details on succesful update
      }
    }
  });

  const voucherSpecs: CreateVoucherRequest = useMemo(
    () => {
      const specs = formik?.values?.voucher_specs ?? null;
      try {
        return specs ? JSON.parse(specs) : null; // if failed parse go to catch below
      } catch {
        snackbar.show({
          severity: 'error',
          message:
            'This event has invalid voucher specs, Please input valid voucher specs'
        });
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik?.values?.voucher_specs]
  );

  // functions
  const createVoucherSpecs = useCallback(() => {
    window.open(`/app/vouchers/create`);
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'VOUCHER_SPECS_CREATED') {
        const newSpecs = event.data.payload;
        formik.setFieldValue('voucher_specs', JSON.stringify(newSpecs));
        snackbar.show({
          severity: 'success',
          message: 'Voucher specs successfully loaded from new tab!'
        });
      }
    },
    [formik, snackbar]
  );

  const handleCreateVoucherSpecs = useCallback(() => {
    const newTab = window.open(
      '/app/vouchers/create?mode=voucher-spec',
      '_blank'
    );

    window.addEventListener('message', handleMessage);

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
    };
    window.addEventListener('beforeunload', cleanup);
  }, [handleMessage]);

  const getVoucherType = (value: string) => {
    const { purchaseIcon, shippingIcon } = constants;
    if (value === purchaseIcon) {
      return 'Purchase Discount';
    }
    if (value === shippingIcon) {
      return 'Shipping Discount';
    }
    return 'Unknown Voucher Type';
  };

  const handleAppname = (value: string) => {
    return value
      .split(',')
      .map((v) => v.trim())
      .join(', ');
  };

  const handleScopes = (arr: VoucherScope[]) => {
    return arr.map((i) => i.scope).join(', ');
  };

  const handlePaste = async () => {
    const voucherSpecs = await navigator.clipboard.readText();

    try {
      const parsed = JSON.parse(voucherSpecs); // intentional parse to trigger catch if failed
      formik.setFieldValue('voucher_specs', voucherSpecs);
    } catch {
      snackbar.show({
        severity: 'error',
        message: 'Clipboard does not contain an valid voucher spec'
      });
    }
  };

  // components
  const SpecField: FC<SpecFieldProps> = ({ label, value, xs = 6 }) => (
    <Grid item xs={xs}>
      <Typography>
        <strong>{label}:</strong> {value || ''}
      </Typography>
    </Grid>
  );

  useEffect(() => {
    getEmailerList({ asOptions: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      getEventDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (eventDetails) {
      formik.resetForm({ values: eventDetails });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetails]);

  return (
    <Card
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2em',
        padding: '1em'
      }}
    >
      <Grid className="text-fields" container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="title"
            label="Title"
            fullWidth
            variant="outlined"
            value={formik?.values?.title ?? ''}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            disabled={isLoading}
            options={emailerList}
            value={
              emailerList.find((e) => e.id === formik?.values?.email_id) ?? null
            }
            getOptionLabel={(option: EmailerListItem) => option?.title ?? ''}
            onChange={(_, newVal) => {
              formik.setFieldValue('email_id', newVal?.id ?? null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Emailer Template"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <DatePickerRangeComponentV3
            fromDateValue={formik.values.start_date}
            size="medium"
            toDateValue={formik.values.end_date}
            onChangeToDate={(date) => {
              const formattedDate = moment(date, [
                'MMMM D, YYYY hh:mm:ss A',
                'YYYY-MM-DD HH:mm:ss'
              ]).format('YYYY-MM-DD HH:mm:ss');
              formik.setFieldValue('end_date', formattedDate);
            }}
            onChangeFromDate={(date) => {
              const formattedDate = moment(date, [
                'MMMM D, YYYY hh:mm:ss A',
                'YYYY-MM-DD HH:mm:ss'
              ]).format('YYYY-MM-DD HH:mm:ss');
              formik.setFieldValue('start_date', formattedDate);
              formik.setFieldValue('end_date', null);
            }}
          />
        </Grid>

        {id && (
          <Grid item xs={12}>
            <Button
              onClick={() => setIsOpenDescriptionModal(!isOpenDescriptionModal)}
              variant="contained"
              color="primary"
              fullWidth
              style={{
                height: 53
              }}
            >
              Edit Event Description
            </Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider style={{ width: '100%', marginTop: 10, marginBottom: 10 }} />
        </Grid>
        <Grid item xs={12}>
          <Accordion expanded={Boolean(voucherSpecs?.name)}>
            <AccordionSummary
              // expandIcon={<ExpandMoreIcon />}
              style={{
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #ddd'
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography variant="h5" color="textPrimary">
                  Voucher Specs
                </Typography>

                <Box display="flex" style={{ gap: '1em' }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleCreateVoucherSpecs();
                    }}
                    variant="outlined"
                    color="primary"
                  >
                    Create New Voucher Specs
                  </Button>
                  {/* <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handlePaste();
                    }}
                    variant="contained"
                    color="primary"
                  >
                    Paste Specs from Clipboard
                  </Button> */}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails style={{ padding: 0 }}>
              <Card
                style={{ padding: '1em', width: '100%', boxShadow: 'none' }}
              >
                <Grid className="voucher-specs-container" container spacing={2}>
                  <SpecField label="Name" value={voucherSpecs?.name} />
                  <SpecField label="Code" value={voucherSpecs?.code} />
                  <SpecField
                    label="Type"
                    value={getVoucherType(voucherSpecs?.voucher_icon_url ?? '')}
                  />
                  <SpecField
                    label="Discount Type"
                    value={voucherSpecs?.discount_type}
                  />
                  <SpecField
                    label="Start Date"
                    value={formatingDate(voucherSpecs?.start_date ?? '')}
                  />
                  <SpecField
                    label="End Date"
                    value={formatingDate(voucherSpecs?.end_date ?? '')}
                  />
                  <SpecField
                    label="Min Spend"
                    value={voucherSpecs?.min_spend}
                  />
                  <SpecField label="Amount" value={voucherSpecs?.amount} />
                  <SpecField
                    label="Cap Amount"
                    value={voucherSpecs?.cap_amount}
                  />
                  <SpecField
                    label="App Name"
                    value={handleAppname(voucherSpecs?.app_name ?? '')}
                  />
                  <SpecField
                    label="Scope"
                    value={handleScopes(voucherSpecs?.voucher_scopes ?? [])}
                  />
                  <SpecField
                    label="Affiliate"
                    value={handleScopes(voucherSpecs?.voucher_scopes ?? [])}
                  />
                  <SpecField
                    label="Description"
                    value={voucherSpecs?.description}
                    xs={12}
                  />
                </Grid>
              </Card>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <Grid
        className="buttons-container"
        container
        spacing={2}
        style={{ justifyContent: 'end' }}
      >
        {/* TODO: get description component and map values to formik */}
        {/* {isCreateForm ? null : (
          <Grid item xs={12} sm={3}>
            <Button
              onClick={() => setIsOpenDescriptionModal(!isOpenDescriptionModal)}
              variant="outlined"
              color="primary"
              fullWidth
              style={{ padding: '1rem' }}
            >
              create/update event description
            </Button>
          </Grid>
        )} */}
      </Grid>

      <Grid
        className="save-button-container"
        container
        spacing={2}
        style={{ display: 'flex', justifyContent: 'end' }}
      >
        <Grid item xs={12} sm={3}>
          <Button
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            style={{ padding: '1rem' }}
            disabled={dynamicSaveLabel === 'Save Changes' && !canUpdateEvent}
          >
            {dynamicSaveLabel}
          </Button>
        </Grid>
      </Grid>

      <DescriptionHtmlDialog
        title="Event Description"
        isOpen={isOpenDescriptionModal}
        handleClose={() => setIsOpenDescriptionModal(false)}
        description={decodeBase64Html(formik.values.description_html) || ''}
        productId={id ?? undefined}
        reloadProductDetails={() => {}}
        variant={'event'}
      />
    </Card>
  );
};
